/**
 * Group Layer Configuration
 *
 * Phase 2: Group Management System
 *
 * Provides group-based filtering and area polygon overlays for the MapComponent.
 *
 * Features:
 * - Group area polygons from GeoJSON data
 * - Filter sensors by group visibility
 * - Point-in-polygon assignment for area groups
 * - Configurable fill opacity and stroke width
 * - Color-coded group visualization
 *
 * @see https://maplibre.org/maplibre-style-spec/layers/#fill
 */

import type {
  FillLayerSpecification,
  LineLayerSpecification,
  ExpressionSpecification,
} from 'maplibre-gl';
import type {
  MapGroup,
  MapGroupFeature,
  MapGroupGeoJSON,
  SensorDataPoint,
} from '../MapComponent.types';
import { isPointInPolygon } from './zoneLayer';

// ============================================================================
// Layer IDs
// ============================================================================

export const GROUP_AREA_SOURCE_ID = 'group-areas';
export const GROUP_AREA_FILL_LAYER_ID = 'group-area-fill';
export const GROUP_AREA_STROKE_LAYER_ID = 'group-area-stroke';

// ============================================================================
// Group Data Processing
// ============================================================================

/**
 * Convert area-type groups to GeoJSON FeatureCollection
 *
 * Only includes groups with type 'area' and valid areaCoordinates
 *
 * @param groups - Array of map groups
 * @returns GeoJSON FeatureCollection for group areas
 */
export const convertGroupsToGeoJSON = (groups: MapGroup[]): MapGroupGeoJSON => {
  // Filter to only area groups with coordinates
  const areaGroups = groups.filter(
    (group) => group.type === 'area' && group.areaCoordinates && group.areaCoordinates.length > 0
  );

  const features: MapGroupFeature[] = areaGroups.map((group) => ({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: group.areaCoordinates!,
    },
    properties: {
      id: group.id,
      name: group.name,
      color: group.color,
      visible: group.visible,
    },
  }));

  return {
    type: 'FeatureCollection',
    features,
  };
};

// ============================================================================
// Paint Configuration
// ============================================================================

/**
 * Get group fill paint configuration
 *
 * Uses the group's color property for fill, with configurable opacity
 *
 * @param config - Configuration with fillOpacity
 * @returns MapLibre fill paint specification
 */
export const getGroupFillPaint = (config: {
  fillOpacity: number;
}): FillLayerSpecification['paint'] => {
  return {
    'fill-color': ['get', 'color'] as ExpressionSpecification,
    'fill-opacity': [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      Math.min(1, config.fillOpacity + 0.15),
      config.fillOpacity,
    ] as ExpressionSpecification,
  };
};

/**
 * Get group stroke (outline) paint configuration
 *
 * Uses a slightly darker version of the group color for borders
 *
 * @param config - Configuration with strokeWidth
 * @returns MapLibre line paint specification
 */
export const getGroupStrokePaint = (config: {
  strokeWidth: number;
}): LineLayerSpecification['paint'] => ({
  'line-color': ['get', 'color'] as ExpressionSpecification,
  'line-width': [
    'case',
    ['boolean', ['feature-state', 'hover'], false],
    config.strokeWidth + 1,
    config.strokeWidth,
  ] as ExpressionSpecification,
  'line-opacity': 0.8,
});

// ============================================================================
// Sensor Filtering Functions
// ============================================================================

/**
 * Filter sensors by group visibility
 *
 * Sensors are shown if:
 * - They belong to at least one visible group (via sensorIds), OR
 * - They are inside a visible area group (point-in-polygon), OR
 * - No groups are defined (show all), OR
 * - No visibleGroups filter is applied (show all)
 *
 * @param sensors - Array of sensor data points
 * @param groups - Array of map groups
 * @param visibleGroups - Set of visible group IDs (or undefined for all visible)
 * @returns Filtered array of sensors
 */
export const filterSensorsByGroups = (
  sensors: SensorDataPoint[],
  groups: MapGroup[],
  visibleGroups: Set<string> | undefined
): SensorDataPoint[] => {
  // If no groups defined, show all sensors
  if (!groups || groups.length === 0) {
    return sensors;
  }

  // If no visibility filter, check each group's visible property
  const effectiveVisibleGroups = visibleGroups ?? new Set(
    groups.filter((g) => g.visible).map((g) => g.id)
  );

  // If all groups are visible, no filtering needed
  if (effectiveVisibleGroups.size === groups.length) {
    return sensors;
  }

  // If no groups are visible, hide all sensors
  if (effectiveVisibleGroups.size === 0) {
    return [];
  }

  // Build lookup maps for efficient filtering
  const sensorToGroups = new Map<string, string[]>();
  const areaGroups: MapGroup[] = [];

  for (const group of groups) {
    if (!effectiveVisibleGroups.has(group.id)) continue;

    // Track sensor-type and custom groups by sensorIds
    if (group.sensorIds && group.sensorIds.length > 0) {
      for (const sensorId of group.sensorIds) {
        const existing = sensorToGroups.get(sensorId) || [];
        existing.push(group.id);
        sensorToGroups.set(sensorId, existing);
      }
    }

    // Track area groups for point-in-polygon checks
    if (group.type === 'area' && group.areaCoordinates) {
      areaGroups.push(group);
    }
  }

  // Filter sensors
  const filtered = sensors.filter((sensor) => {
    // Check if sensor is explicitly in a visible group
    if (sensorToGroups.has(sensor.id)) {
      return true;
    }

    // Check if sensor is inside any visible area group
    for (const areaGroup of areaGroups) {
      if (
        areaGroup.areaCoordinates &&
        isPointInPolygon(
          [sensor.longitude, sensor.latitude],
          areaGroup.areaCoordinates
        )
      ) {
        return true;
      }
    }

    return false;
  });

  return filtered;
};

/**
 * Assign sensors to area groups based on point-in-polygon
 *
 * Returns a map of sensor ID to array of group IDs the sensor is within
 *
 * @param sensors - Array of sensor data points
 * @param groups - Array of map groups
 * @returns Map of sensor ID to containing group IDs
 */
export const assignSensorsToAreaGroups = (
  sensors: SensorDataPoint[],
  groups: MapGroup[]
): Map<string, string[]> => {
  const result = new Map<string, string[]>();

  // Get only area groups with coordinates
  const areaGroups = groups.filter(
    (g) => g.type === 'area' && g.areaCoordinates && g.areaCoordinates.length > 0
  );

  if (areaGroups.length === 0) {
    return result;
  }

  for (const sensor of sensors) {
    const containingGroups: string[] = [];

    for (const group of areaGroups) {
      if (
        isPointInPolygon(
          [sensor.longitude, sensor.latitude],
          group.areaCoordinates!
        )
      ) {
        containingGroups.push(group.id);
      }
    }

    if (containingGroups.length > 0) {
      result.set(sensor.id, containingGroups);
    }
  }

  return result;
};

// ============================================================================
// Default Export
// ============================================================================

export default {
  convertGroupsToGeoJSON,
  getGroupFillPaint,
  getGroupStrokePaint,
  filterSensorsByGroups,
  assignSensorsToAreaGroups,
  GROUP_AREA_SOURCE_ID,
  GROUP_AREA_FILL_LAYER_ID,
  GROUP_AREA_STROKE_LAYER_ID,
};
